<template>
  <a
    :id="getId"
    :ref="getId"
    class="al-link-button"
    :href="href ? href : 'javascript:;'"
    :target="target ? target : ''"
    :download="isDownload"
    :disabled="isDisabled"
    :class="computedClass"
    @click="click"
    @focus="$emit('focus')"
  >
    <!-- Icon -->
    <al-icon v-if="icon" :icon="icon" class="al-button_icon al-icon" />
    <!-- Text -->
    <span v-if="$slots.default || text" class="al-button_text">
      <!--@slot slot used to put html inside button-->
      <slot>{{ text }}</slot>
    </span>
  </a>
</template>
<script>
import AlIcon from '../../inline/icon/AlIcon.vue';
/**
 * Playvox link button with a past definition
 *
 * @displayName al-link-button
 */
export default {
  name: 'AlLinkButton',
  // -------------------------------------------------
  // + Life Cicle
  // -------------------------------------------------
  components: {
    AlIcon
  },
  props: {
    /**
     * Element Identify
     */
    id: {
      type: String,
      default: ''
    },
    /**
     * It's an alternative to send the text shown in the link button programmatically.
     */
    text: {
      type: String,
      default: ''
    },
    /**
     * Defined what HTML tag will be rendered.
     * @values submit, button
     */
    type: {
      type: String,
      default: ''
    },
    /**
     * hyperlink for link tag where the user will be taken with click action.
     * @values URL encoded
     */
    href: {
      type: String,
      default: ''
    },
    /**
     * target for link tag where the user will be taken with click action.
     * @values URL encoded
     */
    target: {
      type: String,
      default: ''
    },
    /**
     * Boolean flag to enable or disable the link button
     * @values true, false
     */
    isDisabled: {
      type: Boolean,
      default: false
    },
    /**
     * Boolean flag to enable or disable the download button
     * @values true, false
     */
    isDownload: {
      type: Boolean,
      default: false
    },
    /**
     * Boolean flag to render button as secondary type.
     * @values true, false
     */
    isSecondary: Boolean,
    /**
     * Boolean flag to render button as secondary gray type with gray border and color.
     * @values true, false
     */
    isSecondaryGray: Boolean,
    /**
     * Boolean flag to render button as secondary type.
     * @values true, false
     */
    isPrimaryRed: Boolean,
    /**
     * Boolean flag to render button as primary red type with red border and color.
     * @values true, false
     */
    isSecondaryRed: Boolean,
    /**
     * Boolean flag to render button as white type.
     * @values true, false
     */
    isWhite: Boolean,
    /**
     * Boolean flag to render the ghost button
     * @values true, false
     */
    isGhost: Boolean,
    /**
     * Boolean flag to set a success state
     * @values true, false
     */
    isSuccess: Boolean,
    /**
     * Boolean flag to make button in full width size
     * @values true, false
     */
    isFullWidth: Boolean,
    size: {
      type: String,
      default: ''
    },
    /**
     * Boolean flag to make the link button small
     * @values true, false
     */
    isSmall: Boolean,
    /**
     * Boolean flag to make the link button extra small
     * @values true, false
     */
    isLarge: Boolean,
    /**
     * @ignore
     */
    isRounded: Boolean,
    /**
     * css class to attach and icon in the link button
     * @values css class selector
     */
    icon: {
      type: String,
      default: ''
    },
    variant: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      blurStatus: null
    };
  },
  computed: {
    getId() {
      // eslint-disable-next-line no-underscore-dangle
      const id = this.id ? this.id : this._uid;
      return `button-${id}`;
    },
    computedClass() {
      const isSecoundary = this.includesAny(this.variant, ['dark', 'secondary']);
      const isGhost = this.includesAny(this.variant, ['ghost']);

      return [
        {
          'is-secondary': this.isSecondary || isSecoundary,
          'is-secondary-gray': this.isSecondaryGray,
          'is-primary-red': this.isPrimaryRed,
          'is-secondary-red': this.isSecondaryRed,
          'is-white': this.isWhite,
          'is-ghost': this.isGhost || isGhost,
          'is-full-width': this.isFullWidth,
          'is-disabled': this.isDisabled,
          'is-rounded': this.isRounded,
          'is-large': this.isLarge || this.size === 'lg',
          'is-xs': this.isXs || this.size === 'sm',
          'has-icon': this.icon && !(!this.$slots.default && !this.text),
          'is-only-icon': this.icon && !this.$slots.default && !this.text
        }
      ];
    },
    contains(word_array = []) {
      return word_array.some((word) => this.includes(word));
    }
  },
  methods: {
    click() {
      const self = this;
      self.$emit('click');
      self.blurStatus = setTimeout(() => {
        clearTimeout(self.blurStatus);
        self.$refs[self.getId].blur();
      }, 300);
    },
    includesAny(str, subStrings) {
      return subStrings.some((word) => str.includes(word));
    }
  }
};
</script>

<style lang="scss">
/**
* Variables
*/

// main
$h-default: 44px;
$p-default: 0 16px;
$font-size-default: 16px;
$p-ghost-default: 0 16px;

// props default
$color-default: #fff;
$bg-default: #009865;
$border-default: 1px solid #00593b;
$br-default: 4px;
$bs-default: 0 0 0 0 #fff;

// props default hover
$color-default-hover: #fff;
$bg-default-hover: #12b981;
$border-default-hover: 1px solid #009865;
$bs-default-hover: 0 8px 4px -4px rgba(54, 64, 63, 0.1);

// props default active
$color-default-active: #5be3b5;
$bg-default-active: #12b981;
$border-default-active: 1px solid #009865;
$bs-default-active: 0 4px 4px 0 rgba(0, 0, 0, 0.05);

// props default disabled
$color-default-disabled: #9b9f9f;
$bg-default-disabled: #d9e3e2;
$border-default-disabled: 1px solid #9b9f9f;

// props default focus
$bs-default-focus: 0 0 0 4px #c2f2e2;

//xs
$h-xs: 36px;
$p-xs: 0 12px;
$font-size-xs: 12.8px;
$p-ghost-xs: 0 12px;

// props xs disabled
$color-xs-disabled: #fff;
$bg-xs-disabled: #d9e3e2;
$border-xs-disabled: 1px solid #d9e3e2;

//lg
$h-lg: 54px;
$p-lg: 0 40px;
$font-size-lg: 20px;
$p-ghost-lg: 0 24px;

// props ghost
$color-ghost: #009865;
$bg-ghost: transparent;
// props ghost hover
$color-ghost-hover: #00593b;
$bg-ghost-hover: #dff7ef;
// props ghost active
$color-ghost-active: #12b981;
$bg-ghost-active: #c2f2e2;
// props ghost disabled
$color-ghost-disabled: #9b9f9f;
$bg-ghost-disabled: transparent;
// props ghost-xs disabled
$color-ghost-xs-disabled: #d9e3e2;
$bg-ghost-xs-disabled: transparent;

// props secondary
$color-secondary: #009865;
$bg-secondary: #fff;
$border-secondary: 1px solid #009865;
// props secondary hover
$color-secondary-hover: #009865;
$bg-secondary-hover: #dff7ef;
$border-secondary-hover: 1px solid #009865;
// props secondary active
$color-secondary-active: #12b981;
$bg-secondary-active: #c2f2e2;
$border-secondary-active: 1px solid #12b981;
// props secondary disabled
$color-secondary-disabled: #9b9f9f;
$bg-secondary-disabled: #f5f8f7;
$border-secondary-disabled: 1px solid #9b9f9f;
// props secondary-xs disabled
$color-secondary-xs-disabled: #fff;
$bg-secondary-xs-disabled: #d9e3e2;
$border-secondary-xs-disabled: 1px solid #d9e3e2;

// props secondary-ghost
$color-secondary-ghost: #36403f;
// props secondary-ghost hover
$color-secondary-ghost-hover: #00593b;
$bg-secondary-ghost-hover: #dff7ef;
// props secondary-ghost active
$color-secondary-ghost-active: #12b981;
$bg-secondary-ghost-active: #c2f2e2;
// props secondary-ghost disabled
$color-secondary-ghost-disabled: #9b9f9f;
$bg-secondary-ghost-disabled: transparent;
// props secondary-ghost-xs disabled
$color-secondary-ghost-xs-disabled: #d9e3e2;
$bg-secondary-ghost-xs-disabled: transparent;

// props secondary-gray
$color-secondary-gray: #9b9f9f;
$bg-secondary-gray: #fff;
$border-secondary-gray: 1px solid #9b9f9f;
// props secondary-gray hover
$color-secondary-gray-hover: #009865;
$bg-secondary-gray-hover: #dff7ef;
$border-secondary-gray-hover: 1px solid #009865;
// props secondary-gray active
$color-secondary-gray-active: #12b981;
$bg-secondary-gray-active: #c2f2e2;
$border-secondary-gray-active: 1px solid #12b981;
// props secondary-gray disabled
$color-secondary-gray-disabled: #9b9f9f;
$bg-secondary-gray-disabled: #f5f8f7;
$border-secondary-gray-disabled: 1px solid #9b9f9f;
// props secondary-gray-xs disabled
$color-secondary-gray-xs-disabled: #fff;
$bg-secondary-gray-xs-disabled: #d9e3e2;
$border-secondary-gray-xs-disabled: 1px solid #d9e3e2;

// props secondary-gray-ghost
$color-secondary-gray-ghost: #9b9f9f;
// props secondary-gray-ghost hover
$color-secondary-gray-ghost-hover: #009865;
$bg-secondary-gray-ghost-hover: #dff7ef;
// props secondary-gray-ghost active
$color-secondary-gray-ghost-active: #12b981;
$bg-secondary-gray-ghost-active: #c2f2e2;
// props secondary-gray-ghost disabled
$color-secondary-gray-ghost-disabled: #9b9f9f;
$bg-secondary-gray-ghost-disabled: transparent;
// props secondary-gray-ghost-xs disabled
$color-secondary-gray-ghost-xs-disabled: #d9e3e2;
$bg-secondary-gray-ghost-xs-disabled: transparent;

// props primary-red
$color-primary-red: #fceeee;
$bg-primary-red: #e85a5a;
$border-primary-red: 1px solid #c23e3e;
// props primary-red hover
$color-primary-red-hover: #fff;
$bg-primary-red-hover: #c23e3e;
$border-primary-red-hover: 1px solid #5e1c1c;
// props primary-red active
$color-primary-red-active: #f19c9c;
$bg-primary-red-active: #c23e3e;
$border-primary-red-active: 1px solid #5e1c1c;
// props primary-red disabled
$color-primary-red-disabled: #9b9f9f;
$bg-primary-red-disabled: #d9e3e2;
$border-primary-red-disabled: 1px solid #9b9f9f;
// props primary-red-xs disabled
$color-primary-red-xs-disabled: #fff;
$bg-primary-red-xs-disabled: #d9e3e2;
$border-primary-red-xs-disabled: 1px solid #d9e3e2;
// props primary-red focus
$bs-primary-red-focus: 0 0 0 4px rgba(248, 205, 205, 1);

// props primary-red-ghost
$color-primary-red-ghost: #c23e3e;
// props primary-red-ghost hover
$color-primary-red-ghost-hover: #c23e3e;
$bg-primary-red-ghost-hover: #f8cdcd;
// props primary-red-ghost active
$color-primary-red-ghost-active: #f19c9c;
$bg-primary-red-ghost-active: #f8cdcd;
// props primary-red-ghost disabled
$color-primary-red-ghost-disabled: #9b9f9f;
$bg-primary-red-ghost-disabled: transparent;
// props primary-red-ghost-xs disabled
$color-primary-red-ghost-xs-disabled: #d9e3e2;
$bg-primary-red-ghost-xs-disabled: transparent;

// props secondary-red
$color-secondary-red: #c23e3e;
$bg-secondary-red: #fff;
$border-secondary-red: 1px solid #c23e3e;
// props secondary-red hover
$color-secondary-red-hover: #e85a5a;
$bg-secondary-red-hover: #fceeee;
$border-secondary-red-hover: 1px solid #c23e3e;
// props secondary-red active
$color-secondary-red-active: #ec7b7b;
$bg-secondary-red-active: #f8cdcd;
$border-secondary-red-active: 1px solid #c23e3e;
// props secondary-red disabled
$color-secondary-red-disabled: #9b9f9f;
$bg-secondary-red-disabled: #f5f8f7;
$border-secondary-red-disabled: 1px solid #9b9f9f;
// props secondary-red-xs disabled
$color-secondary-red-xs-disabled: #fff;
$bg-secondary-red-xs-disabled: #d9e3e2;
$border-secondary-red-xs-disabled: 1px solid #d9e3e2;
// props secondary-red focus
$bs-secondary-red-focus: 0 0 0 4px rgba(248, 205, 205, 1);

// props secondary-red-ghost
$color-secondary-red-ghost: #36403f;
// props secondary-red-ghost hover
$color-secondary-red-ghost-hover: #c23e3e;
$bg-secondary-red-ghost-hover: #f8cdcd;
// props secondary-red-ghost active
$color-secondary-red-ghost-active: #f19c9c;
$bg-secondary-red-ghost-active: #f8cdcd;
// props secondary-red-ghost disabled
$color-secondary-red-ghost-disabled: #9b9f9f;
$bg-secondary-red-ghost-disabled: transparent;
// props secondary-red-ghost-xs disabled
$color-secondary-red-ghost-xs-disabled: #d9e3e2;
$bg-secondary-red-ghost-xs-disabled: transparent;

// props white
$color-white: #fff;
$bg-white: transparent;
$border-white: 1px solid rgba(#fff, 0.1);
// props white hover
$color-white-hover: #fff;
$bg-white-hover: rgba(#fff, 0.1);
$border-white-hover: 1px solid rgba(#fff, 0.1);
// props white active
$color-white-active: #fff;
$bg-white-active: rgba(#fff, 0.2);
$border-white-active: 1px solid rgba(#fff, 0.2);
// props white disabled
$color-white-disabled: #9b9f9f;
$bg-white-disabled: #d9e3e2;
$border-white-disabled: 1px solid #9b9f9f;
// props white-xs disabled
$color-white-xs-disabled: #fff;
$bg-white-xs-disabled: #d9e3e2;
$border-white-xs-disabled: 1px solid #d9e3e2;
// props white focus
$bs-white-focus: 0 0 0 4px rgba(255, 255, 255, 0.5);
// props white-ghost hover
$color-white-ghost-hover: #fff;
$bg-white-ghost-hover: rgba(#fff, 0.1);
// props white-ghost active
$color-white-ghost-active: #fff;
$bg-white-ghost-active: rgba(#fff, 0.2);
// props white-ghost disabled
$color-white-ghost-disabled: #9b9f9f;
$bg-white-ghost-disabled: transparent;
// props white-ghost-xs disabled
$color-white-ghost-xs-disabled: #d9e3e2;
$bg-white-ghost-xs-disabled: transparent;

.al-link-button {
  align-items: center;
  border-style: solid;
  box-sizing: border-box;
  cursor: pointer;

  display: inline-flex;
  justify-content: center;
  outline: none;
  text-align: center;
  transition: background-color 0.3s;

  &:active,
  &:focus,
  &:hover,
  &:visited {
    outline: none;
    text-decoration: none;
  }

  &:focus,
  &:active {
    transition: background-color 0.3s, box-shadow 0.2s;
  }

  &:first-child {
    margin-left: 0;
  }

  &:last-child {
    margin-right: 0;
  }

  > * {
    outline: none;
  }

  /** main */

  &,
  &.is-default {
    background-color: $bg-default;
    border: $border-default;
    border-radius: $br-default;
    box-shadow: $bs-default;

    // props default
    color: $color-default;
    font-size: $font-size-default;
    height: $h-default;
    padding: $p-default;
    // size
    width: auto;

    &.is-hover,
    &:hover {
      background-color: $bg-default-hover;
      border: $border-default-hover;
      box-shadow: $bs-default-hover;
      // props default hover
      color: $color-default-hover;
    }

    &.is-active,
    &:active {
      background-color: $bg-default-active;
      border: $border-default-active;
      box-shadow: $bs-default-active;
      // props default active
      color: $color-default-active;
    }

    &.is-disabled,
    &:disabled {
      background-color: $bg-default-disabled;
      border: $border-default-disabled;

      box-shadow: none;
      // props default disabled
      color: $color-default-disabled;
      cursor: not-allowed;
    }

    &:focus:active {
      // props default focus active
      box-shadow: none;
    }

    &:focus:not(:active):not(:visited) {
      // props default focus
      box-shadow: $bs-default-focus;
    }
    /** variations */

    // ghost
    &.is-ghost {
      background-color: $bg-ghost;
      // props ghost
      color: $color-ghost;

      &.is-hover,
      &:hover {
        background-color: $bg-ghost-hover;
        // props ghost hover
        color: $color-ghost-hover;
      }

      &.is-active,
      &:active {
        background-color: $bg-ghost-active;
        // props ghost active
        color: $color-ghost-active;
      }

      &.is-disabled,
      &:disabled {
        background-color: $bg-ghost-disabled;
        // props ghost disabled
        color: $color-ghost-disabled;
      }

      &.is-xs {
        &.is-disabled,
        &:disabled {
          background-color: $bg-ghost-xs-disabled;
          border-width: 0;
          // props ghost-xs disabled
          color: $color-ghost-xs-disabled;
        }
      }
    }

    // secondary
    &.is-secondary {
      background-color: $bg-secondary;
      border: $border-secondary;
      // props secondary
      color: $color-secondary;

      &.is-hover,
      &:hover {
        background-color: $bg-secondary-hover;
        border: $border-secondary-hover;
        // props secondary hover
        color: $color-secondary-hover;
      }

      &.is-active,
      &:active {
        background-color: $bg-secondary-active;
        border: $border-secondary-active;
        // props secondary active
        color: $color-secondary-active;
      }

      &.is-disabled,
      &:disabled {
        background-color: $bg-secondary-disabled;
        border: $border-secondary-disabled;
        // props secondary disabled
        color: $color-secondary-disabled;
      }

      &.is-xs {
        &.is-disabled,
        &:disabled {
          background-color: $bg-secondary-xs-disabled;
          border: $border-secondary-xs-disabled;
          // props secondary-xs disabled
          color: $color-secondary-xs-disabled;
        }
      }

      &.is-ghost {
        border-width: 0;
        // props secondary-ghost
        color: $color-secondary-ghost;

        &.is-hover,
        &:hover {
          background-color: $bg-secondary-ghost-hover;
          // props secondary-ghost hover
          color: $color-secondary-ghost-hover;
        }

        &.is-active,
        &:active {
          background-color: $bg-secondary-ghost-active;
          // props secondary-ghost active
          color: $color-secondary-ghost-active;
        }

        &.is-disabled,
        &:disabled {
          background-color: $bg-secondary-ghost-disabled;
          // props secondary-ghost disabled
          color: $color-secondary-ghost-disabled;
        }

        &.is-xs {
          &.is-disabled,
          &:disabled {
            background-color: $bg-secondary-ghost-xs-disabled;
            border-width: 0;
            // props secondary-ghost-xs disabled
            color: $color-secondary-ghost-xs-disabled;
          }
        }
      }
    }

    // secondary-gray
    &.is-secondary-gray {
      background-color: $bg-secondary-gray;
      border: $border-secondary-gray;
      // props secondary-gray
      color: $color-secondary-gray;

      &.is-hover,
      &:hover {
        background-color: $bg-secondary-gray-hover;
        border: $border-secondary-gray-hover;
        // props secondary-gray hover
        color: $color-secondary-gray-hover;
      }

      &.is-active,
      &:active {
        background-color: $bg-secondary-gray-active;
        border: $border-secondary-gray-active;
        // props secondary-gray active
        color: $color-secondary-gray-active;
      }

      &.is-disabled,
      &:disabled {
        background-color: $bg-secondary-gray-disabled;
        border: $border-secondary-gray-disabled;
        // props secondary-gray disabled
        color: $color-secondary-gray-disabled;
      }

      &.is-xs {
        &.is-disabled,
        &:disabled {
          background-color: $bg-secondary-gray-xs-disabled;
          border: $border-secondary-gray-xs-disabled;
          // props secondary-gray-xs disabled
          color: $color-secondary-gray-xs-disabled;
        }
      }

      &.is-ghost {
        border-width: 0;
        // props secondary-gray-ghost
        color: $color-secondary-gray-ghost;

        &.is-hover,
        &:hover {
          background-color: $bg-secondary-gray-ghost-hover;
          // props secondary-gray-ghost hover
          color: $color-secondary-gray-ghost-hover;
        }

        &.is-active,
        &:active {
          background-color: $bg-secondary-gray-ghost-active;
          // props secondary-gray-ghost active
          color: $color-secondary-gray-ghost-active;
        }

        &.is-disabled,
        &:disabled {
          background-color: $bg-secondary-gray-ghost-disabled;
          // props secondary-gray-ghost disabled
          color: $color-secondary-gray-ghost-disabled;
        }

        &.is-xs {
          &.is-disabled,
          &:disabled {
            background-color: $bg-secondary-gray-ghost-xs-disabled;
            border-width: 0;
            // props secondary-gray-ghost-xs disabled
            color: $color-secondary-gray-ghost-xs-disabled;
          }
        }
      }
    }

    // primary-red
    &.is-primary-red {
      background-color: $bg-primary-red;
      border: $border-primary-red;
      // props primary-red
      color: $color-primary-red;

      &.is-hover,
      &:hover {
        background-color: $bg-primary-red-hover;
        border: $border-primary-red-hover;
        // props primary-red hover
        color: $color-primary-red-hover;
      }

      &.is-active,
      &:active {
        background-color: $bg-primary-red-active;
        border: $border-primary-red-active;
        // props primary-red active
        color: $color-primary-red-active;
      }

      &.is-disabled,
      &:disabled {
        background-color: $bg-primary-red-disabled;
        border: $border-primary-red-disabled;
        // props primary-red disabled
        color: $color-primary-red-disabled;
      }

      &.is-xs {
        &.is-disabled,
        &:disabled {
          background-color: $bg-primary-red-xs-disabled;
          border: $border-primary-red-xs-disabled;
          // props primary-red-xs disabled
          color: $color-primary-red-xs-disabled;
        }
      }

      &:focus:not(:active):not(:visited) {
        // props primary-red focus
        box-shadow: $bs-primary-red-focus;
      }

      &.is-ghost {
        background-color: transparent;
        border-width: 0;
        // props primary-red-ghost
        color: $color-primary-red-ghost;

        &.is-hover,
        &:hover {
          background-color: $bg-primary-red-ghost-hover;
          // props primary-red-ghost hover
          color: $color-primary-red-ghost-hover;
        }

        &.is-active,
        &:active {
          background-color: $bg-primary-red-ghost-active;
          // props primary-red-ghost active
          color: $color-primary-red-ghost-active;
        }

        &.is-disabled,
        &:disabled {
          background-color: $bg-primary-red-ghost-disabled;
          // props primary-red-ghost disabled
          color: $color-primary-red-ghost-disabled;
        }

        &.is-xs {
          &.is-disabled,
          &:disabled {
            background-color: $bg-primary-red-ghost-xs-disabled;
            border-width: 0;
            // props primary-red-ghost-xs disabled
            color: $color-primary-red-ghost-xs-disabled;
          }
        }
      }
    }

    // secondary-red
    &.is-secondary-red {
      background-color: $bg-secondary-red;
      border: $border-secondary-red;
      // props secondary-red
      color: $color-secondary-red;

      &.is-hover,
      &:hover {
        background-color: $bg-secondary-red-hover;
        border: $border-secondary-red-hover;
        // props secondary-red hover
        color: $color-secondary-red-hover;
      }

      &.is-active,
      &:active {
        background-color: $bg-secondary-red-active;
        border: $border-secondary-red-active;
        // props secondary-red active
        color: $color-secondary-red-active;
      }

      &.is-disabled,
      &:disabled {
        background-color: $bg-secondary-red-disabled;
        border: $border-secondary-red-disabled;
        // props secondary-red disabled
        color: $color-secondary-red-disabled;
      }

      &.is-xs {
        &.is-disabled,
        &:disabled {
          background-color: $bg-secondary-red-xs-disabled;
          border: $border-secondary-red-xs-disabled;
          // props secondary-red-xs disabled
          color: $color-secondary-red-xs-disabled;
        }
      }

      &:focus:not(:active):not(:visited) {
        // props secondary-red focus
        box-shadow: $bs-secondary-red-focus;
      }

      &.is-ghost {
        background-color: transparent;
        border-width: 0;
        // props secondary-red-ghost
        color: $color-secondary-red-ghost;

        &.is-hover,
        &:hover {
          background-color: $bg-secondary-red-ghost-hover;
          // props secondary-red-ghost hover
          color: $color-secondary-red-ghost-hover;
        }

        &.is-active,
        &:active {
          background-color: $bg-secondary-red-ghost-active;
          // props secondary-red-ghost active
          color: $color-secondary-red-ghost-active;
        }

        &.is-disabled,
        &:disabled {
          background-color: $bg-secondary-red-ghost-disabled;
          // props secondary-red-ghost disabled
          color: $color-secondary-red-ghost-disabled;
        }

        &.is-xs {
          &.is-disabled,
          &:disabled {
            background-color: $bg-secondary-red-ghost-xs-disabled;
            border-width: 0;
            // props secondary-red-ghost-xs disabled
            color: $color-secondary-red-ghost-xs-disabled;
          }
        }
      }
    }

    // white
    &.is-white {
      background-color: $bg-white;
      border: $border-white;
      // props white
      color: $color-white;

      &.is-hover,
      &:hover {
        background-color: $bg-white-hover;
        border: $border-white-hover;
        // props white hover
        color: $color-white-hover;
      }

      &.is-active,
      &:active {
        background-color: $bg-white-active;
        border: $border-white-active;
        // props white active
        color: $color-white-active;
      }

      &.is-disabled,
      &:disabled {
        background-color: $bg-white-disabled;
        border: $border-white-disabled;
        // props white disabled
        color: $color-white-disabled;
      }

      &.is-xs {
        &.is-disabled,
        &:disabled {
          background-color: $bg-white-xs-disabled;
          border: $border-white-xs-disabled;
          // props white-xs disabled
          color: $color-white-xs-disabled;
        }
      }

      &:focus:not(:active):not(:visited) {
        // props white focus
        box-shadow: $bs-white-focus;
      }

      &.is-ghost {
        // props white-ghost
        // props white-ghost
        $color-white-ghost: #fff;
        background-color: transparent;
        border-width: 0;
        color: $color-white-ghost;

        &.is-hover,
        &:hover {
          background-color: $bg-white-ghost-hover;
          // props white-ghost hover
          color: $color-white-ghost-hover;
        }

        &.is-active,
        &:active {
          background-color: $bg-white-ghost-active;
          // props white-ghost active
          color: $color-white-ghost-active;
        }

        &.is-disabled,
        &:disabled {
          background-color: $bg-white-ghost-disabled;
          // props white-ghost disabled
          color: $color-white-ghost-disabled;
        }

        &.is-xs {
          &.is-disabled,
          &:disabled {
            background-color: $bg-white-ghost-xs-disabled;
            border-width: 0;
            // props white-ghost-xs disabled
            color: $color-white-ghost-xs-disabled;
          }
        }
      }
    }

    .al-button_icon {
      margin-right: 8px;
    }

    &.is-only-icon {
      max-width: $h-default;
      padding: 0 9px;

      .al-button_icon {
        margin-right: 0;
      }
    }

    &.is-rounded {
      border-radius: $h-default;
    }
  }

  &.is-xs {
    font-size: $font-size-xs;
    font-weight: bold;
    height: $h-xs;
    letter-spacing: 0.2px;
    padding: $p-xs;
    text-transform: uppercase;

    &,
    &.is-ghost,
    &:hover {
      border-width: 0;
    }

    &:not(.is-ghost) {
      &.is-secondary,
      &.is-secondary-gray,
      &.is-secondary-red {
        border-width: 1px;

        &:hover {
          border-width: 1px;
        }
      }
    }

    &.is-disabled,
    &:disabled {
      background-color: $bg-xs-disabled;
      border: $border-xs-disabled;
      // props xs disabled
      color: $color-xs-disabled;
    }

    .al-button_icon {
      margin-right: 4px;
    }

    &.is-only-icon {
      max-width: $h-xs;
      padding: 0 6px;

      .al-button_icon {
        margin-right: 0;
      }
    }

    &.is-rounded {
      border-radius: $h-xs;
    }
  }

  &.is-large {
    font-size: $font-size-lg;
    height: $h-lg;
    line-height: 24px;
    padding: $p-lg;

    .al-button_icon {
      margin-right: 12px;
    }

    &.is-only-icon {
      max-width: $h-lg;
      padding: 0 14px;

      .al-button_icon {
        margin-right: 0;
      }
    }

    &.is-rounded {
      border-radius: $h-lg;
    }
  }

  /** is-ghost */

  &.is-ghost {
    border-width: 0;
    padding: $p-ghost-default;

    &:hover {
      border-width: 0;
    }

    &.is-xs {
      padding: $p-ghost-xs;
    }

    &.is-large {
      padding: $p-ghost-lg;
    }
  }

  /** is-block */

  &.is-block {
    display: block;
    padding: 0 auto;
    width: 100%;
  }

  /** has-icon */

  &.has-icon {
    &,
    &.is-default {
      padding: 0 12px 0 10px;
    }

    &.is-xs {
      padding: 0 12px 0 6px;
    }

    &.is-large {
      padding: 0 20px 0 16px;
    }
  }

  .al-button_icon {
    height: 24px;
    width: 24px;
  }
}
</style>
